import { GameCard } from "../types/battle"
import {
  MailMarketActionResponse,
  MailMarketLot,
  MailMarketLotsResponse,
  MailMarketMineResponse,
  MailMarketPriceBounds,
  MailMarketPurchaseResponse,
} from "../types/mailMarket"
import { normalizeGameCard } from "./gameCards"

type RawRecord = Record<string, unknown>

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNumber(value: unknown, fallback: number | null = null): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.trim().replace(",", "."))
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickIsoDate(value: unknown): string | null {
  if (value == null || value === "") return null
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || null
  }
  return null
}

function unwrapPayload(data: unknown): RawRecord {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as RawRecord
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawRecord), ...obj }
  }
  return obj
}

function normalizePriceBounds(raw: unknown): MailMarketPriceBounds | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const minPrice = pickNumber(
    item.minPriceCoins ?? item.min_price_coins ?? item.min
  )
  const maxPrice = pickNumber(
    item.maxPriceCoins ?? item.max_price_coins ?? item.max
  )
  const sellPrice = pickNumber(
    item.sellPriceCoins ?? item.sell_price_coins ?? item.sellPrice
  )
  if (minPrice == null || maxPrice == null) return null
  return {
    minPriceCoins: minPrice,
    maxPriceCoins: maxPrice,
    sellPriceCoins: sellPrice ?? minPrice,
  }
}

export function normalizeMailMarketLot(raw: unknown): MailMarketLot | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const id = item.id ?? item.uuid ?? item.lotId ?? item.lot_id
  const cardId = pickString(item.cardId ?? item.card_id)
  if (id == null || !cardId) return null
  const card =
    normalizeGameCard(item.card) ??
    normalizeGameCard(item.offeredCard ?? item.offered_card) ??
    null
  const boundsRaw = item.priceBounds ?? item.price_bounds
  return {
    id: String(id),
    cardId: card?.id ?? cardId,
    card,
    priceCoins: pickNumber(item.priceCoins ?? item.price_coins, 0) ?? 0,
    priceBounds: normalizePriceBounds(boundsRaw),
    sellerUserId: pickNumber(item.sellerUserId ?? item.seller_user_id),
    sellerNickname:
      pickString(item.sellerNickname ?? item.seller_nickname) || null,
    sellerPublicCode:
      pickString(item.sellerPublicCode ?? item.seller_public_code) || null,
    pointId: pickString(item.pointId ?? item.point_id) || null,
    expiresAt: pickIsoDate(item.expiresAt ?? item.expires_at),
    createdAt: pickIsoDate(item.createdAt ?? item.created_at),
  }
}

function mapLots(data: unknown): MailMarketLot[] {
  if (Array.isArray(data)) {
    return data
      .map(normalizeMailMarketLot)
      .filter((lot): lot is MailMarketLot => lot != null)
  }
  const obj = unwrapPayload(data)
  const list = obj.lots ?? obj.items ?? obj.marketLots ?? obj.market_lots
  if (Array.isArray(list)) {
    return list
      .map(normalizeMailMarketLot)
      .filter((lot): lot is MailMarketLot => lot != null)
  }
  const single = normalizeMailMarketLot(obj)
  return single ? [single] : []
}

export function normalizeMailMarketLotsResponse(
  data: unknown
): MailMarketLotsResponse {
  const obj = unwrapPayload(data)
  const globalPts = pickNumber(obj.globalPts ?? obj.global_pts)
  const marketLockedMinPts =
    pickNumber(obj.marketLockedMinPts ?? obj.market_locked_min_pts, 3000) ?? 3000
  const marketLocked =
    obj.marketLocked === true ||
    obj.market_locked === true ||
    (globalPts != null && globalPts < marketLockedMinPts)

  return {
    lots: mapLots(obj.lots ?? obj.items ?? data),
    globalPts: globalPts ?? undefined,
    marketLocked,
    marketLockedMinPts,
  }
}

export function normalizeMailMarketMineResponse(
  data: unknown
): MailMarketMineResponse {
  const obj = unwrapPayload(data)
  return {
    lots: mapLots(obj.lots ?? obj.items ?? data),
    activeLotsCount:
      pickNumber(obj.activeLotsCount ?? obj.active_lots_count, 0) ?? 0,
    activeLotsLimit:
      pickNumber(obj.activeLotsLimit ?? obj.active_lots_limit, 2) ?? 2,
    sellsToday: pickNumber(obj.sellsToday ?? obj.sells_today, 0) ?? 0,
    sellsDailyLimit:
      pickNumber(obj.sellsDailyLimit ?? obj.sells_daily_limit, 3) ?? 3,
    buysToday: pickNumber(obj.buysToday ?? obj.buys_today, 0) ?? 0,
    buysDailyLimit:
      pickNumber(obj.buysDailyLimit ?? obj.buys_daily_limit, 3) ?? 3,
  }
}

export function normalizeMailMarketPurchaseResponse(
  data: unknown
): MailMarketPurchaseResponse | null {
  const obj = unwrapPayload(data)
  const lot = normalizeMailMarketLot(obj.lot ?? obj)
  if (!lot) return null
  return {
    lot,
    priceCoins: pickNumber(obj.priceCoins ?? obj.price_coins, lot.priceCoins) ?? 0,
    commissionCoins:
      pickNumber(obj.commissionCoins ?? obj.commission_coins, 0) ?? 0,
    sellerReceives:
      pickNumber(obj.sellerReceives ?? obj.seller_receives, 0) ?? 0,
    championFundCoins:
      pickNumber(obj.championFundCoins ?? obj.champion_fund_coins, 0) ?? 0,
    fundDelta: pickNumber(obj.fundDelta ?? obj.fund_delta, 0) ?? 0,
    buysToday: pickNumber(obj.buysToday ?? obj.buys_today, 0) ?? 0,
    buysDailyLimit:
      pickNumber(obj.buysDailyLimit ?? obj.buys_daily_limit, 3) ?? 3,
  }
}

export function normalizeMailMarketActionResponse(
  data: unknown
): MailMarketActionResponse {
  const obj = unwrapPayload(data)
  return {
    lot: normalizeMailMarketLot(obj.lot ?? obj),
    championFundCoins:
      pickNumber(obj.championFundCoins ?? obj.champion_fund_coins) ?? undefined,
    fundDelta: pickNumber(obj.fundDelta ?? obj.fund_delta) ?? undefined,
  }
}

export function collectMarketListedCardIds(lots: MailMarketLot[]): Set<string> {
  const ids = new Set<string>()
  for (const lot of lots) {
    if (lot.cardId) ids.add(lot.cardId)
  }
  return ids
}

export function filterCardsForMarketOffer(
  cards: GameCard[],
  escrowedIds: Set<string>,
  marketListedIds: Set<string>,
  activeCardId?: string | null
): GameCard[] {
  return cards.filter(
    (card) =>
      card.id &&
      card.storedInBank !== true &&
      card.listedOnMarket !== true &&
      card.id !== activeCardId &&
      !escrowedIds.has(card.id) &&
      !marketListedIds.has(card.id) &&
      rankIndex(card.colorRank) >= rankIndex("GREEN")
  )
}

function rankIndex(rank: import("../types/battle").ColorRank): number {
  const order: import("../types/battle").ColorRank[] = [
    "WHITE",
    "GREEN",
    "BLUE",
    "PURPLE",
    "ORANGE",
    "RED",
  ]
  const idx = order.indexOf(rank)
  return idx >= 0 ? idx : 0
}
